<template>
  <div class="monthly-summary" :class="{ active }" @click="onClick">
    <div class="title">
      <div class="month">{{ month }}</div>
      <div
        class="forecast-sla"
        v-featureflow:isOn="'capacity-planner-forecast-sla'"
      >
        <ForecastGauge :value="slaCount"></ForecastGauge>
      </div>
    </div>
    <div class="metrics">
      <div class="title-right">
        <div>
          <strong>{{ unitOfWorkString }}</strong>
        </div>
      </div>
      <hr class="line-seperator" />
      <!-- Required -->
      <div class="metric-row fte-required">
        <div class="metric-title">
          <AlTooltip
            :message="getTooltipMessage('required-FTE')"
            position="bottom"
            test-id="required"
          >
            <span class="icon icon-circle"><RequiredFTE /></span>
          </AlTooltip>
          <Localization message="monthly-summary-required" />
        </div>
        <div class="score" data-testid="required-score">
          {{ requiredFormatted }}
        </div>
      </div>
      <!-- Leave -->
      <div class="metric-row leave">
        <div class="metric-title">
          <AlTooltip
            :message="getTooltipMessage('leave-FTE')"
            position="bottom"
            test-id="leave"
          >
            <span class="icon outline icon-circle"><Plane /></span>
          </AlTooltip>
          <Localization message="monthly-summary-leave" />
        </div>
        <div class="score" data-testid="leave-score">
          {{ leaveTotalFormatted }}
        </div>
      </div>
      <!-- Shrinkage -->
      <div class="metric-row shrinkage">
        <div class="metric-title">
          <AlTooltip
            :message="getTooltipMessage('shrinkage-FTE')"
            position="bottom"
            test-id="shrinkage"
          >
            <span class="icon icon-circle"><Shrinkage /></span>
          </AlTooltip>
          <Localization message="monthly-summary-shrinkage" />
        </div>
        <div class="score" data-testid="shrinkage-score">
          {{ shrinkageFormatted }}
        </div>
      </div>
      <!-- Available -->
      <div class="metric-row fte-available">
        <div class="metric-title">
          <AlTooltip
            :message="getTooltipMessage('available-FTE')"
            position="bottom"
            test-id="available"
          >
            <span class="icon icon-circle"><Available /></span>
          </AlTooltip>
          <Localization message="monthly-summary-available" />
        </div>
        <div class="score" data-testid="available-score">
          {{ availableFormatted }}
        </div>
      </div>
      <!-- Difference -->
      <div
        :class="[positiveNegativeMetricClass, 'metric-row', 'fte-difference']"
      >
        <div class="metric-title">
          <AlTooltip
            :message="getTooltipMessage('difference-FTE')"
            position="bottom"
            test-id="difference"
          >
            <span class="icon icon-circle"><Variance /></span>
          </AlTooltip>
          <Localization message="monthly-summary-difference" />
        </div>
        <div class="score" data-testid="difference-score">
          {{ differenceFormatted }}
        </div>
      </div>
      <hr />
      <div
        class="metric-row footer"
        v-featureflow:isOn="'capacity-planner-headcount'"
      >
        <AlTooltip
          :message="getTooltipMessage('estimated-headcount')"
          position="bottom"
          test-id="headcount"
        >
          <Localization message="monthly-summary-headcount" />
        </AlTooltip>
        <div class="score" data-testid="headcount-score">
          {{ headcountFormatted }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Localization, Translator, Icons } from "@agyletime/wfm-vue-common";
import { utcToZonedTime } from "date-fns-tz";
import { AlTooltip } from "@playvox/almendra-components";
import { round, getAvailable, getLeave } from "../utils/math";
import ForecastGauge from "./charts/MonthChartCard/ForecastGauge.vue";

const { Variance, Shrinkage, RequiredFTE, Plane, Available } = Icons;

const oneDecimalPlace = 1;
const displayStatuses = ["COMPLETE"];
const loadingMessage = "Loading";

export default {
  name: "MonthlySummary",
  mixins: [Translator],
  components: {
    Localization,
    Variance,
    Shrinkage,
    RequiredFTE,
    Plane,
    Available,
    AlTooltip,
    ForecastGauge,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      required: true,
    },
    timezone: {
      type: String,
      required: true,
    },
    isHours: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: "",
    },
    showNaRequired: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    positiveNegativeMetricClass() {
      return this.difference < 0 ? "red" : "green";
    },
    month() {
      const zonedDate = utcToZonedTime(this.data.date, this.timezone);

      return this.getValueInLocale(zonedDate, {
        month: "long",
      });
    },
    unitOfWorkString() {
      return this.isHours ? "Hours" : "FTE";
    },
    headcount() {
      return this.data.headcount || 0;
    },
    headcountFormatted() {
      return this.formatNumberWithDecimal(this.headcount, 0);
    },
    difference() {
      //We're using formatted so that the figures in the UI add up
      // e.g. The customer desire is that 1.04 + 1.04 = 2.08 results in 1 + 1 = 2, not 1 + 1 = 2.1
      const available = round(this.available, oneDecimalPlace);
      const required = round(
        this.showNaRequired ? 0 : this.required,
        oneDecimalPlace
      );
      return available - required;
    },
    differenceFormatted() {
      if (displayStatuses.indexOf(this.status) === -1 && !this.difference) {
        return loadingMessage;
      }
      return this.formatNumberWithDecimal(this.difference);
    },
    required() {
      if (this.data && this.data.required) {
        let { hours, fte } = this.data.required;
        return (this.isHours ? hours : fte) || 0;
      }
      return 0;
    },
    requiredFormatted() {
      return this.showNaRequired
        ? "N/A"
        : this.formatNumberWithDecimal(this.required);
    },
    startingAvailable() {
      if (this.data && this.data.available) {
        let { hours, fte } = this.data.available;
        return (this.isHours ? hours : fte) || 0;
      }
      return 0;
    },
    available() {
      return getAvailable(
        this.startingAvailable,
        this.leaveTotal,
        this.shrinkage
      );
    },
    availableFormatted() {
      if (displayStatuses.indexOf(this.status) === -1 && !this.available) {
        return loadingMessage;
      }

      return this.formatNumberWithDecimal(this.available);
    },
    shrinkage() {
      if (this.data && this.data.shrinkage) {
        let { hours, fte } = this.data.shrinkage;
        return (this.isHours ? hours : fte) || 0;
      }
      return 0;
    },
    shrinkageFormatted() {
      if (displayStatuses.indexOf(this.status) === -1 && !this.shrinkage) {
        return loadingMessage;
      }

      return this.formatNumberWithDecimal(this.shrinkage);
    },
    plannedLeave() {
      if (this.data && this.data.plannedLeave) {
        let { hours, fte } = this.data.plannedLeave;
        return (this.isHours ? hours : fte) || 0;
      }
      return 0;
    },
    unplannedLeave() {
      if (this.data && this.data.unplannedLeave) {
        let { hours, fte } = this.data.unplannedLeave;
        return (this.isHours ? hours : fte) || 0;
      }
      return 0;
    },
    leaveTotal() {
      return getLeave(this.plannedLeave, this.unplannedLeave);
    },
    leaveTotalFormatted() {
      if (displayStatuses.indexOf(this.status) === -1 && !this.leaveTotal) {
        return loadingMessage;
      }

      return this.formatNumberWithDecimal(this.leaveTotal);
    },
    slaCount() {
      return ((this.data.sla || 0) * 100).toFixed(0);
    },
  },
  methods: {
    formatNumberWithDecimal(num, decimal = oneDecimalPlace) {
      return num && !Number.isNaN(Number.parseFloat(num))
        ? Number(num).toFixed(decimal)
        : 0;
    },
    getTooltipMessage(message) {
      return this.translate(message);
    },
    onClick() {
      this.$emit("click");
    },
  },
};
</script>

<style scoped lang="scss">
.monthly-summary {
  --border-radius: 10px;
  --card-spacing: 1rem;

  height: 100%;
  border: solid 1px var(--color-eerie-black-20);
  border-radius: var(--border-radius);
  box-shadow: 0px 4px 8px -4px var(--color-eerie-black-30);
  transition: box-shadow 0.3s;
  margin: 0.25rem;
  margin-bottom: 1rem;
  max-width: 240px;
  min-width: 240px;
  margin-right: 1rem;

  &:hover {
    box-shadow: 0px 8px 8px 0px var(--color-eerie-black-40);
    cursor: pointer;
  }

  &.active {
    background-color: var(--color-eerie-black-20);
    box-shadow: 0px 4px 8px -4px var(--color-eerie-black-40);

    &:hover {
      box-shadow: 0px 8px 8px -2px var(--color-eerie-black-40);
      cursor: pointer;
    }

    .title {
      color: var(--color-eerie-black-20);
      background-color: var(--color-ocean-blue-70);
    }
  }

  hr {
    border-color: var(--color-eerie-black-30);
    margin: 0.25rem 0;
    margin-bottom: 0.75rem;
  }

  .title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 15px;
    border-top-left-radius: var(--border-radius);
    border-top-right-radius: var(--border-radius);
    background-color: var(--color-ocean-blue-60);
    color: var(--color-white);
    .month {
      font-size: 1.5rem;
      font-weight: bold;
    }
  }

  .metrics {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    padding: calc(var(--card-spacing) / 2) var(--card-spacing);
    white-space: nowrap;

    & > * {
      display: flex;
      justify-content: space-between;

      &.metric-row:not(:first-child) {
        margin-bottom: 0.25rem;
      }
    }

    .metric-row {
      &.red .score {
        color: var(--color-red-salsa-40);
      }
      &.green .score {
        color: var(--color-green-pantone-40);
      }
    }

    .metric-title {
      display: flex;
      align-items: center;
    }

    .score {
      display: flex;
      align-items: center;
      margin-left: 1rem;
    }

    .icon-circle {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 1.2rem;
      height: 1.2rem;
      border-radius: 50%;
      margin-right: 0.5rem;
      background-color: var(--icon-background);

      & > svg {
        width: 0.7rem;
        height: 0.7rem;
      }
    }

    .icon-stack {
      position: relative;
    }

    .fte-difference {
      --icon-background: var(--color-red-salsa-40);
    }
    .fte-required {
      --icon-background: var(--color-maize-crayola-40);
    }
    .fte-available {
      --icon-background: var(--color-green-pantone-40);
    }
    .shrinkage {
      --icon-background: var(--color-deep-violet-40);
    }
    .leave {
      --icon-background: var(--color-sky-blue-40);
    }
  }

  .title-right {
    display: flex;
    & > * {
      margin-bottom: calc(var(--card-spacing) / 4);
      margin-left: auto;
    }
  }
}
.dr-tooltip__message-container {
  max-width: 200px !important;
}
</style>
